import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { databasePromise } from "configuration";
import { useAuthentication } from "./useAuthentication";
import { useLike } from "./useLike";
import { reviewService } from "services";

export const useFavorites = (sortBy = "newest", contentFilter = "both") => {
  const [deals, setDeals] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [likedReviews, setLikedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuthentication();

  const { like: likeBaseDeal, unlike: unlikeBaseDeal } = useLike("deals");
  const { like: likeBaseRestaurant, unlike: unlikeBaseRestaurant } =
    useLike("restaurants");

  // Single effect — all three collections fire in parallel, one loading/error pair
  useEffect(() => {
    if (!user?.uid) {
      setDeals([]);
      setRestaurants([]);
      setLikedReviews([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    databasePromise.then(async (db) => {
      try {
        const dealsQuery =
          contentFilter === "both" || contentFilter === "deals"
            ? getDocs(
                query(
                  collection(db, "deals"),
                  where("likedBy", "array-contains", user.uid),
                  where("status", "in", [
                    "active",
                    "pendingCreate",
                    "pendingEdit",
                    "pendingDelete",
                    "disabled",
                  ]),
                ),
              )
            : Promise.resolve({ docs: [] });

        const restaurantsQuery =
          contentFilter === "both" || contentFilter === "restaurants"
            ? getDocs(
                query(
                  collection(db, "restaurants"),
                  where("likedBy", "array-contains", user.uid),
                  where("status", "in", [
                    "active",
                    "pendingCreate",
                    "pendingEdit",
                    "disabled",
                  ]),
                ),
              )
            : Promise.resolve({ docs: [] });

        const reviewsQuery = getDocs(
          query(
            collection(db, "reviews"),
            where("likedBy", "array-contains", user.uid),
            where("status", "in", [
              "approved",
              "pendingEdit",
              "pendingDelete",
              "pendingReport",
            ]),
          ),
        );

        const [dealsSnapshot, restaurantsSnapshot, reviewsSnapshot] =
          await Promise.all([dealsQuery, restaurantsQuery, reviewsQuery]);

        // restaurantStatus is not yet denormalised onto deals — treat
        // missing field as active (null-safe). Only exclude deals explicitly
        // marked as belonging to a deleted restaurant.
        const filteredDeals = dealsSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (deal) =>
              !deal.restaurantStatus ||
              !["deleted", "pendingDelete"].includes(deal.restaurantStatus),
          );

        // Locations omitted intentionally — saves N subcollection reads.
        // The restaurant page shows full location detail.
        // TODO: restore if locations are needed on favourites cards.
        const mappedRestaurants = restaurantsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const mappedReviews = reviewsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setDeals(filteredDeals);
        setRestaurants(mappedRestaurants);
        setLikedReviews(mappedReviews);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching favorites:", err);
        setError(err.message);
        setLoading(false);
      }
    });
  }, [user?.uid, contentFilter]);

  // Deal like/unlike with optimistic updates
  const likeDeal = async (dealId) => {
    await likeBaseDeal(dealId);
    setDeals((prev) =>
      prev.map((deal) =>
        deal.id === dealId
          ? { ...deal, likedBy: [...(deal.likedBy || []), user.uid] }
          : deal,
      ),
    );
  };

  const unlikeDeal = async (dealId) => {
    await unlikeBaseDeal(dealId);
    setDeals((prev) => prev.filter((deal) => deal.id !== dealId));
  };

  // Restaurant like/unlike with optimistic updates
  const likeRestaurant = async (restaurantId) => {
    await likeBaseRestaurant(restaurantId);
    setRestaurants((prev) =>
      prev.map((r) =>
        r.id === restaurantId
          ? { ...r, likedBy: [...(r.likedBy || []), user.uid] }
          : r,
      ),
    );
  };

  const unlikeRestaurant = async (restaurantId) => {
    await unlikeBaseRestaurant(restaurantId);
    setRestaurants((prev) => prev.filter((r) => r.id !== restaurantId));
  };

  // Review like/unlike with optimistic updates
  const unlikeReview = useCallback(
    async (reviewId) => {
      try {
        await reviewService.removeLike(reviewId, user.uid);
        setLikedReviews((prev) => prev.filter((r) => r.id !== reviewId));
      } catch (err) {
        console.error("Error unliking review:", err);
      }
    },
    [user?.uid],
  );

  const likeReview = useCallback(
    async (reviewId) => {
      try {
        await reviewService.addLike(reviewId, user.uid);
        setLikedReviews((prev) =>
          prev.map((r) =>
            r.id === reviewId
              ? { ...r, likedBy: [...(r.likedBy || []), user.uid] }
              : r,
          ),
        );
      } catch (err) {
        console.error("Error liking review:", err);
      }
    },
    [user?.uid],
  );

  // All three collections sorted by the same sortBy prop
  const sortedFavorites = useMemo(() => {
    const sortItems = (items) => {
      const sorted = [...items];
      switch (sortBy) {
        case "newest":
          sorted.sort(
            (a, b) =>
              (b.createdDate?.toDate?.() || new Date(0)) -
              (a.createdDate?.toDate?.() || new Date(0)),
          );
          break;
        case "oldest":
          sorted.sort(
            (a, b) =>
              (a.createdDate?.toDate?.() || new Date(0)) -
              (b.createdDate?.toDate?.() || new Date(0)),
          );
          break;
        case "mostLiked":
          sorted.sort(
            (a, b) =>
              (Array.isArray(b.likedBy) ? b.likedBy.length : 0) -
              (Array.isArray(a.likedBy) ? a.likedBy.length : 0),
          );
          break;
        case "highestRated":
          sorted.sort(
            (a, b) =>
              (typeof b.rating === "number" ? b.rating : 0) -
              (typeof a.rating === "number" ? a.rating : 0),
          );
          break;
        default:
          break;
      }
      return sorted;
    };

    return {
      deals: sortItems(deals),
      restaurants: sortItems(restaurants),
      likedReviews: sortItems(likedReviews),
    };
  }, [deals, restaurants, likedReviews, sortBy]);

  return {
    deals: sortedFavorites.deals,
    restaurants: sortedFavorites.restaurants,
    likedReviews: sortedFavorites.likedReviews,
    loading,
    error,
    likeDeal,
    unlikeDeal,
    likeRestaurant,
    unlikeRestaurant,
    likeReview,
    unlikeReview,
  };
};
